import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 900, margin: '0 auto' },
  backLink: { color: '#0078d4', cursor: 'pointer', fontSize: 14, marginBottom: 16, display: 'inline-block', background: 'none', border: 'none', padding: 0 },
  title: { fontSize: 28, fontWeight: 700, color: '#1a365d', margin: '0 0 8px' },
  subtitle: { color: '#666', fontSize: 14, margin: '0 0 32px' },
  badge: { display: 'inline-block', background: '#ebf8ff', color: '#2b6cb0', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4, marginLeft: 10, verticalAlign: 'middle' },
  toc: { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '20px 24px', marginBottom: 32 },
  tocTitle: { fontSize: 14, fontWeight: 700, color: '#1a365d', margin: '0 0 12px' },
  tocList: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' },
  tocLink: { color: '#0078d4', fontSize: 13, cursor: 'pointer', textDecoration: 'none', padding: '2px 0', display: 'block' },
  section: { marginBottom: 36 },
  sectionTitle: { fontSize: 20, fontWeight: 700, color: '#1a365d', margin: '0 0 12px', paddingBottom: 8, borderBottom: '2px solid #e2e8f0' },
  subsection: { fontSize: 16, fontWeight: 600, color: '#2d3748', margin: '20px 0 8px' },
  p: { fontSize: 14, lineHeight: 1.7, color: '#4a5568', margin: '0 0 12px' },
  ul: { paddingLeft: 20, margin: '0 0 12px' },
  li: { fontSize: 14, lineHeight: 1.7, color: '#4a5568', marginBottom: 4 },
  tip: { background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#2a4365' },
  warning: { background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#92400e' },
  searchBox: { width: '100%', padding: '10px 14px', border: '1px solid #cbd5e0', borderRadius: 8, fontSize: 14, marginBottom: 24, boxSizing: 'border-box' as const },
};

const SECTIONS = [
  { id: 'overview', title: 'Accounting Overview' },
  { id: 'dashboard-tab', title: 'Dashboard Tab' },
  { id: 'events-tab', title: 'Events Tab' },
  { id: 'admins-tab', title: 'Admins Tab' },
  { id: 'logs-tab', title: 'Logs Tab' },
  { id: 'reports-section', title: 'Report Management' },
  { id: 'workflows', title: 'Common Workflows' },
];

export default function AccountingHelpPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const matchesSearch = (text: string) => {
    if (!search) return true;
    return text.toLowerCase().includes(search.toLowerCase());
  };

  return (
    <div style={styles.container}>
      <button style={styles.backLink} onClick={() => navigate(-1)}>
        &larr; Back
      </button>

      <h1 style={styles.title}>
        Accounting Documentation
        <span style={styles.badge}>Admin Only</span>
      </h1>
      <p style={styles.subtitle}>The full guide to the Accounting tab &mdash; charts, events, admin management, and all the knobs you didn't know you had.</p>

      <input
        style={styles.searchBox}
        placeholder="Search accounting docs..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {!search && (
        <div style={styles.toc}>
          <h2 style={styles.tocTitle}>Table of Contents</h2>
          <ul style={styles.tocList}>
            {SECTIONS.map(s => (
              <li key={s.id}>
                <a style={styles.tocLink} onClick={() => scrollTo(s.id)}>{s.title}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Overview */}
      {matchesSearch('overview accounting admin organization visibility expenses') && (
        <div style={styles.section} id="overview">
          <h2 style={styles.sectionTitle}>Accounting Overview</h2>
          <p style={styles.p}>
            The Accounting tab is your command center for seeing expenses across the entire organization. If you're reading this, you're an admin &mdash; congratulations on the power, and the responsibility that comes with it.
          </p>
          <p style={styles.p}>
            The tab has four sections, each with its own sub-tab: <strong>Dashboard</strong> (the charts and data), <strong>Events</strong> (grouping reports together), <strong>Admins</strong> (managing who has access), and <strong>Logs</strong> (what's been happening behind the scenes). We'll walk through each one.
          </p>
        </div>
      )}

      {/* Dashboard Tab */}
      {matchesSearch('dashboard charts analytics spending month user event filter csv export') && (
        <div style={styles.section} id="dashboard-tab">
          <h2 style={styles.sectionTitle}>Dashboard Tab</h2>
          <p style={styles.p}>
            This is your "where did all the money go?" view. Interactive charts, filterable data, and a detailed receipt table that lets you drill down into the specifics.
          </p>

          <h3 style={styles.subsection}>Charts</h3>
          <p style={styles.p}>You get three charts, each telling a different story:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Spending by Month & User</strong> &mdash; A vertical stacked bar chart showing monthly expenses, color-coded by team member. Good for spotting trends and busy months.</li>
            <li style={styles.li}><strong>By User & Expense Type</strong> &mdash; Horizontal bars showing each person's spending broken down by category (Meals, Airfare, Lodging, etc.). This is the one you look at when you're wondering who's spending what on what.</li>
            <li style={styles.li}><strong>By Event & Expense Type</strong> &mdash; Same idea, but grouped by event. Perfect for seeing the total cost of a conference or client visit.</li>
          </ul>
          <div style={styles.tip}>
            <strong>The charts are interactive.</strong> Click any bar or segment to filter the receipt details table below. Hold Ctrl (or Cmd on Mac) and click to select multiple items. Click again to deselect. It's way more useful than it sounds &mdash; try it.
          </div>

          <h3 style={styles.subsection}>Filters</h3>
          <p style={styles.p}>The filter bar at the top lets you narrow things down by:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Year</strong> &mdash; Quick switch between years (only shows years with actual data)</li>
            <li style={styles.li}><strong>Date Range</strong> &mdash; Custom start/end dates for more precise windows</li>
            <li style={styles.li}><strong>Event</strong> &mdash; Filter to a specific event</li>
            <li style={styles.li}><strong>User</strong> &mdash; Filter to a specific team member</li>
          </ul>
          <p style={styles.p}>
            Filters apply automatically as you change them &mdash; no "Apply" button needed. The charts and receipt table update in real time.
          </p>

          <h3 style={styles.subsection}>Receipt Details Table</h3>
          <p style={styles.p}>
            Below the charts, you get a scrollable table with every individual receipt matching your current filters. Each row shows the date, vendor, category, amount, user, and event. There's a "View" link to preview the actual receipt image, and a total at the bottom.
          </p>

          <h3 style={styles.subsection}>Export CSV</h3>
          <p style={styles.p}>
            Hit "Export CSV" to download the currently filtered receipt data as a spreadsheet. Whatever you see in the table is what you get in the export &mdash; chart filters and all.
          </p>
        </div>
      )}

      {/* Events Tab */}
      {matchesSearch('events tab create auto-generate assign reports conference grouping AI') && (
        <div style={styles.section} id="events-tab">
          <h2 style={styles.sectionTitle}>Events Tab</h2>
          <p style={styles.p}>
            Events are how you group related expense reports together. Think "Esri Dev Summit 2026" or "Client Onsite Houston" &mdash; one event that pulls in all the reports and receipts from everyone who attended.
          </p>

          <h3 style={styles.subsection}>Creating Events</h3>
          <p style={styles.p}>Two ways to go about this:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Manual</strong> &mdash; Click "Create Event," give it a name, start date, and end date. Done.</li>
            <li style={styles.li}><strong>Auto-Generate from Reports</strong> &mdash; This is the fun one. Click the button and AI analyzes every report in the system &mdash; titles, date ranges, the works. It groups reports that look like they belong to the same event (even with slightly different names like "Dev Summit" vs "Esri Developer Summit") and creates events automatically. Reports are assigned to the new events right away.</li>
          </ul>
          <div style={styles.tip}>
            <strong>How auto-generate works under the hood:</strong> Claude Haiku reads all report titles and date ranges, applies fuzzy matching to group similar ones, and creates events with the best name from each group. It's not going to win any AI awards, but it saves a ton of manual work.
          </div>

          <h3 style={styles.subsection}>The Events Table</h3>
          <p style={styles.p}>Each event shows:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Event Name</strong> &mdash; Click to expand and see the full breakdown</li>
            <li style={styles.li}><strong>Start/End Date</strong> &mdash; The event window (editable)</li>
            <li style={styles.li}><strong>Reports</strong> &mdash; How many reports are assigned</li>
            <li style={styles.li}><strong>Total</strong> &mdash; The combined dollar amount from all assigned reports</li>
            <li style={styles.li}><strong>Source</strong> &mdash; "Auto-generated" or "Manual" so you know how it was created</li>
          </ul>

          <h3 style={styles.subsection}>Event Details (Expanded)</h3>
          <p style={styles.p}>
            Click an event name and it expands to show two tables:
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Totals by User</strong> &mdash; Per-person receipt count and dollar total. Quick way to see who spent what.</li>
            <li style={styles.li}><strong>Receipt Details</strong> &mdash; Every individual receipt in the event, with date, vendor, category, amount, and user. Scrollable if there are a lot.</li>
          </ul>

          <h3 style={styles.subsection}>Assigning Reports</h3>
          <p style={styles.p}>
            Below the events table, you'll see a Reports section listing all reports across the organization. Click the Event column on any report to assign it to an event. When you assign a report, all its receipts are automatically linked to that event too &mdash; the cascade is handled for you.
          </p>
        </div>
      )}

      {/* Admins Tab */}
      {matchesSearch('admins tab manage access users add remove search') && (
        <div style={styles.section} id="admins-tab">
          <h2 style={styles.sectionTitle}>Admins Tab</h2>
          <p style={styles.p}>
            This is where you manage who gets to see the Accounting tab. With great power comes great responsibility &mdash; or at least access to everyone's lunch receipts.
          </p>

          <h3 style={styles.subsection}>Adding Admins</h3>
          <p style={styles.p}>
            Start typing a name or email in the search box. The app searches your Azure AD directory and shows matching CGS team members. Click someone to add them. Only <strong>@cultivategeospatial.com</strong> accounts can be added &mdash; no external emails, no exceptions.
          </p>

          <h3 style={styles.subsection}>Removing Admins</h3>
          <p style={styles.p}>
            Click "Remove" next to any admin to revoke their access. They'll lose the Accounting tab immediately. Their own receipts and reports aren't affected &mdash; they just can't see everyone else's anymore.
          </p>
          <div style={styles.warning}>
            <strong>Note:</strong> Seed admins (hardcoded in the system) can't be removed through the UI. If you need to change those, that's a code change.
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {matchesSearch('logs tab activity monitoring events debug') && (
        <div style={styles.section} id="logs-tab">
          <h2 style={styles.sectionTitle}>Logs Tab</h2>
          <p style={styles.p}>
            The Logs tab is your audit trail. It shows a chronological record of everything that's happened in the system &mdash; receipt uploads, report generations, event assignments, admin changes, the works.
          </p>
          <p style={styles.p}>
            This is the tab you go to when someone says "I definitely uploaded that receipt" and you need to verify whether they actually did. (Spoiler: the logs don't lie.)
          </p>
          <p style={styles.p}>
            You can adjust the log capture level to control how much detail gets recorded. Higher levels mean more noise but more visibility.
          </p>
        </div>
      )}

      {/* Report Management */}
      {matchesSearch('reports management assign event receipt cascading total') && (
        <div style={styles.section} id="reports-section">
          <h2 style={styles.sectionTitle}>Report Management</h2>
          <p style={styles.p}>
            The Reports section (at the bottom of the Events tab) gives you a bird's-eye view of every report in the system, across all users. Each row shows the title, employee, date range, total amount, receipt count, and event assignment.
          </p>

          <h3 style={styles.subsection}>How Report-Event Assignment Works</h3>
          <p style={styles.p}>
            When you assign a report to an event (manually or via auto-generate), here's what happens behind the scenes:
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}>The report's <code>EventId</code> is set to the event</li>
            <li style={styles.li}>Every receipt in that report gets its <code>EventId</code> updated too</li>
            <li style={styles.li}>The event's report count and total amount update automatically</li>
          </ul>
          <p style={styles.p}>
            This cascading assignment is why event totals are accurate &mdash; receipts are linked through their reports, not just by date ranges. A receipt for airfare purchased weeks before a conference still shows up under that event if the report it belongs to is assigned correctly.
          </p>
        </div>
      )}

      {/* Common Workflows */}
      {matchesSearch('workflows common end of month conference review process') && (
        <div style={styles.section} id="workflows">
          <h2 style={styles.sectionTitle}>Common Workflows</h2>

          <h3 style={styles.subsection}>After a Conference or Trip</h3>
          <ul style={styles.ul}>
            <li style={styles.li}>Wait for all attendees to submit their reports (give them a gentle nudge if needed)</li>
            <li style={styles.li}>Go to Accounting &rarr; Events &rarr; "Auto-Generate from Reports"</li>
            <li style={styles.li}>Review the generated event &mdash; check the total, verify all reports are assigned</li>
            <li style={styles.li}>If any reports are missing, manually assign them from the Reports table</li>
            <li style={styles.li}>Click the event to expand and review per-user breakdowns</li>
          </ul>

          <h3 style={styles.subsection}>Monthly or Quarterly Review</h3>
          <ul style={styles.ul}>
            <li style={styles.li}>Go to Accounting &rarr; Dashboard</li>
            <li style={styles.li}>Set the date range or select the month/year you want to review</li>
            <li style={styles.li}>Check the "By User" chart for any outliers</li>
            <li style={styles.li}>Click into specific bars to drill down to individual receipts</li>
            <li style={styles.li}>Export CSV if you need to share the data or import it into another system</li>
          </ul>

          <h3 style={styles.subsection}>Adding a New Team Member as Admin</h3>
          <ul style={styles.ul}>
            <li style={styles.li}>Go to Accounting &rarr; Admins</li>
            <li style={styles.li}>Type their name in the search box</li>
            <li style={styles.li}>Click their name from the results</li>
            <li style={styles.li}>They'll have Accounting access the next time they load the app</li>
          </ul>
        </div>
      )}

      <div style={{ padding: '24px 0 48px', borderTop: '1px solid #e2e8f0', marginTop: 32, textAlign: 'center', color: '#999', fontSize: 12 }}>
        CGS Expense Reports &middot; Cultivate Geospatial Solutions
      </div>
    </div>
  );
}
